import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { formatNumber } from '@/lib/format'

interface TierRow {
  tier: string
  count: number
  emails: number
  revenue: number
}

interface TierBreakdownTableProps {
  data: Record<string, { count: number; emails: number; revenue: number }>
}

export function TierBreakdownTable({ data }: TierBreakdownTableProps): React.JSX.Element {
  const rows: TierRow[] = Object.entries(data).map(([tier, stats]) => ({
    tier,
    count: stats.count,
    emails: stats.emails,
    revenue: stats.revenue,
  }))

  const columns = [
    {
      key: 'tier',
      label: 'Tier',
      render: (row: TierRow) => <StatusBadge status={row.tier} />,
    },
    {
      key: 'count',
      label: 'Accounts',
      sortable: true,
      render: (row: TierRow) => formatNumber(row.count),
    },
    {
      key: 'emails',
      label: 'Emails Sent',
      sortable: true,
      render: (row: TierRow) => formatNumber(row.emails),
    },
    {
      key: 'limit',
      label: 'Limit (each)',
      render: (row: TierRow) => formatNumber(getTierLimit(row.tier)),
    },
    {
      key: 'revenue',
      label: 'Revenue',
      sortable: true,
      render: (row: TierRow) => (
        <span className={row.revenue > 0 ? 'font-medium text-green-600' : 'text-text-tertiary'}>
          ${row.revenue.toLocaleString()}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-medium text-text-primary">Accounts by Tier</h2>
      <DataTable columns={columns} data={rows} emptyMessage="No tier data available." />
    </div>
  )
}

function getTierLimit(tier: string): number {
  const limits: Record<string, number> = {
    free: 10000,
    starter: 25000,
    pro: 75000,
    growth: 300000,
    scale: 2000000,
  }
  return limits[tier] ?? 0
}
